<template>
  <div
    class="wago-params-panel"
    :class="{ 'dark-mode': isDarkMode }"
  >
    <!-- Sekcja 1: Parametry podstawowe -->
    <div class="wago-section-label">
      {{ $t('components.assetDetails.wagoParams.sectionBasic') }}
    </div>
    <div class="asset-stats-grid">
      <div
        v-for="row in basicRows"
        :key="row.key"
        class="asset-stat"
      >
        <div class="asset-stat-header">
          <i :class="row.icon" />
          <span>{{ row.label }}</span>
        </div>
        <div class="asset-stat-value">
          {{ row.value }}
        </div>
      </div>
    </div>

    <!-- Sekcja 2: Parametry poszczególnych faz -->
    <div class="wago-section-label">
      {{ $t('components.assetDetails.wagoParams.sectionPhases') }}
    </div>
    <div class="wago-phase-grid">
      <div
        v-for="row in phaseRows"
        :key="row.key"
        class="asset-stat"
      >
        <div class="asset-stat-header">
          <i :class="row.icon" />
          <span>{{ row.label }}</span>
        </div>
        <div class="wago-phase-values">
          <div class="wago-phase-col">
            <span class="wago-phase-label">L1</span>
            <span class="asset-stat-value">{{ row.l1 }}</span>
          </div>
          <div class="wago-phase-col">
            <span class="wago-phase-label">L2</span>
            <span class="asset-stat-value">{{ row.l2 }}</span>
          </div>
          <div class="wago-phase-col">
            <span class="wago-phase-label">L3</span>
            <span class="asset-stat-value">{{ row.l3 }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

export default defineComponent({
  name: 'WagoLatestParamsPanel',
  props: {
    latestParams: {
      type: Array,
      required: true
    },
    isDarkMode: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    basicRows(): Array<{ key: string; icon: string; label: string; value: string }> {
      return [
        {
          key: 'voltage',
          icon: 'bi bi-lightning-charge',
          label: this.$t('components.assetDetails.wagoParams.voltage') as string,
          value: this.formatVal('WAGO_5000_VOLTAGE', 1, 'V')
        },
        {
          key: 'frequency',
          icon: 'bi bi-soundwave',
          label: this.$t('components.assetDetails.wagoParams.frequency') as string,
          value: this.formatVal('WAGO_5008_FREQUENCY', 1, 'Hz')
        },
        {
          key: 'current',
          icon: 'bi bi-activity',
          label: this.$t('components.assetDetails.wagoParams.current') as string,
          value: this.formatVal('WAGO_500A_CURRENT', 1, 'A')
        },
        {
          key: 'powerFactor',
          icon: 'bi bi-speedometer2',
          label: this.$t('components.assetDetails.wagoParams.powerFactor') as string,
          value: this.formatVal('WAGO_502A_PF_TOTAL', 1, '')
        },
        {
          key: 'activePowerTotal',
          icon: 'bi bi-plug-fill',
          label: this.$t('components.assetDetails.wagoParams.activePowerTotal') as string,
          value: this.formatVal('WAGO_6000_E_ACT_TOTAL', 1, 'kWh')
        },
        {
          key: 'reactivePowerTotal',
          icon: 'bi bi-arrow-repeat',
          label: this.$t('components.assetDetails.wagoParams.reactivePowerTotal') as string,
          value: this.formatVal('WAGO_6024_E_REACT_TOTAL', 1, 'kVArh')
        }
      ]
    },
    phaseRows(): Array<{ key: string; icon: string; label: string; l1: string; l2: string; l3: string }> {
      return [
        {
          key: 'voltage',
          icon: 'bi bi-lightning-charge',
          label: this.$t('components.assetDetails.wagoParams.voltage') as string,
          l1: this.formatVal('WAGO_5002_VOLTAGE_L1', 1, 'V'),
          l2: this.formatVal('WAGO_5004_VOLTAGE_L2', 1, 'V'),
          l3: this.formatVal('WAGO_5006_VOLTAGE_L3', 1, 'V')
        },
        {
          key: 'activePower',
          icon: 'bi bi-plug',
          label: this.$t('components.assetDetails.wagoParams.activePower') as string,
          l1: this.formatDelta('WAGO_6006_E_ACT_L1', 'kWh'),
          l2: this.formatDelta('WAGO_6008_E_ACT_L2', 'kWh'),
          l3: this.formatDelta('WAGO_600A_E_ACT_L3', 'kWh')
        },
        {
          key: 'reactivePower',
          icon: 'bi bi-arrow-clockwise',
          label: this.$t('components.assetDetails.wagoParams.reactivePower') as string,
          l1: this.formatDelta('WAGO_602A_E_REACT_L1', 'kVArh'),
          l2: this.formatDelta('WAGO_602C_E_REACT_L2', 'kVArh'),
          l3: this.formatDelta('WAGO_602E_E_REACT_L3', 'kVArh')
        },
        {
          key: 'powerFactor',
          icon: 'bi bi-speedometer2',
          label: this.$t('components.assetDetails.wagoParams.powerFactor') as string,
          l1: this.formatVal('WAGO_502C_PF_L1', 1, ''),
          l2: this.formatVal('WAGO_502E_PF_L2', 1, ''),
          l3: this.formatVal('WAGO_5030_PF_L3', 1, '')
        }
      ]
    }
  },
  methods: {
    findParam(name: string): any {
      const params = Array.isArray(this.latestParams) ? this.latestParams : []
      return (params as any[]).find(
        (p: any) => p?.can_params_io?.param_name === name
      )
    },
    formatVal(name: string, multiplier: number, unit: string): string {
      const param = this.findParam(name)
      if (!param || param.value === null || param.value === undefined) return '—'
      const raw = parseFloat(param.value)
      if (!Number.isFinite(raw)) return '—'
      const val = (raw * multiplier).toFixed(2)
      return unit ? `${val} ${unit}` : val
    },
    formatDelta(name: string, unit: string): string {
      const params = Array.isArray(this.latestParams) ? this.latestParams : []
      const matches = (params as any[]).filter(
        (p: any) => p?.can_params_io?.param_name === name
      )
      if (matches.length < 2) return '—'
      const v1 = parseFloat(matches[0].value)
      const v2 = parseFloat(matches[1].value)
      if (!Number.isFinite(v1) || !Number.isFinite(v2)) return '—'
      const delta = (v1 - v2).toFixed(2)
      return unit ? `${delta} ${unit}` : delta
    }
  }
})
</script>

<style lang="scss" scoped>
.wago-section-label {
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #667085;
  margin: 14px 0 8px;

  &:first-child {
    margin-top: 0;
  }
}

.wago-phase-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.wago-phase-values {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-top: 4px;
}

.wago-phase-col {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.wago-phase-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #a0aab8;
}

/* Dark mode overrides for custom elements only.
   asset-stat / asset-stat-* are handled globally by body.dark-theme */
.dark-mode {
  .wago-section-label {
    color: #b5b5c3;
  }

  .wago-phase-label {
    color: #6b7280;
  }
}
</style>
